import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetNationalHolisticAnalyticsQuery,
  mergeNationalAnalyticsResponses,
  TermFilter,
  getClassDisplayName,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import KVSLoader from '../../../../../components/loaders/KVSLoader';
import { StatBox } from '../../../components/shared';

const COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EF4444'];

const scaleToPercent = (val: number): number => {
  if (val <= 1.0 && val > 0) return +(val * 100).toFixed(1);
  return +val.toFixed(1);
};

const PhysicalAnalysis: React.FC = () => {
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');
  const isOverall = term === 'overall';

  const singleTermQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!effectiveData?.data) return [];
    return effectiveData.data.flatMap((region) => region.records || []);
  }, [effectiveData]);



  const stats = useMemo(() => {
    if (!records.length) {
      return {
        avgPhysical: 0,
        evaluated: 0,
        needsSupport: 0,
        distribution: [
          { category: 'Excellent (>= 85%)', count: 0 },
          { category: 'Good (70-84%)', count: 0 },
          { category: 'Average (55-69%)', count: 0 },
          { category: 'Below Average (< 55%)', count: 0 },
        ],
        studentsList: [],
      };
    }

    let totalPhysical = 0;
    let excellent = 0;
    let good = 0;
    let average = 0;
    let needsSupport = 0;

    const list = records.map((record) => {
      const pScore = scaleToPercent(Number(record.physical_dev_score || 0));
      totalPhysical += pScore;

      let category: 'Excellent' | 'Good' | 'Average' | 'Below Average';
      if (pScore >= 85) {
        excellent++;
        category = 'Excellent';
      } else if (pScore >= 70) {
        good++;
        category = 'Good';
      } else if (pScore >= 55) {
        average++;
        category = 'Average';
      } else {
        needsSupport++;
        category = 'Below Average';
      }

      const first = (record.firstName ?? record.first_name ?? '').trim();
      const middle = (record.middleName ?? record.middle_name ?? '').trim();
      const last = (record.lastName ?? record.last_name ?? '').trim();
      const fullName = [first, middle, last].filter(Boolean).join(' ') || `Student ${record.student_id}`;

      return {
        id: record.student_id,
        name: fullName,
        className: getClassDisplayName(Number(record.class_id)),
        section: '-',
        score: pScore,
        category,
      };
    });

    const len = records.length;
    return {
      avgPhysical: +(totalPhysical / len).toFixed(1),
      evaluated: len,
      needsSupport,
      distribution: [
        { category: 'Excellent (>= 85%)', count: excellent },
        { category: 'Good (70-84%)', count: good },
        { category: 'Average (55-69%)', count: average },
        { category: 'Below Average (< 55%)', count: needsSupport },
      ],
      studentsList: list.slice(0, 50), // Show top 50 in table for performance
    };
  }, [records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Physical Development Analysis</h1>
          <p className="text-sm text-gray-500">Physical development indicators across {records.length} students</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatBox label="Avg Physical Dev Score" value={`${stats.avgPhysical}%`} color="text-indigo-600" large />
        <StatBox label="Students Evaluated" value={`${stats.evaluated}`} color="text-blue-600" large />
        <StatBox label="Needs Support" value={`${stats.needsSupport}`} color="text-emerald-600" large />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Physical Development Score Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={stats.distribution} barSize={56}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="category" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {stats.distribution.map((entry, idx) => (
                <Cell key={entry.category} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Student Physical Data Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Individual Physical Development Data</h3>
          <span className="text-xs text-gray-400">Showing first 50 students</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Student</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Class</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Physical Dev. Score</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Category</th>
              </tr>
            </thead>
            <tbody>
              {stats.studentsList.map((s: any) => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 text-sm font-medium text-gray-900">
                    <div>
                      <p>{s.name}</p>
                      <p className="text-xs text-gray-400">{s.id}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700">{s.className}</td>
                  <td className="px-5 py-3 text-sm text-center text-gray-700 font-semibold">{s.score}%</td>
                  <td className="px-5 py-3 text-center">
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: (s.category === 'Excellent' ? '#10B981' : s.category === 'Good' ? '#6366F1' : s.category === 'Average' ? '#F59E0B' : '#EF4444') + '20',
                        color: s.category === 'Excellent' ? '#10B981' : s.category === 'Good' ? '#6366F1' : s.category === 'Average' ? '#F59E0B' : '#EF4444'
                      }}
                    >
                      {s.category}
                    </span>
                  </td>
                </tr>
              ))}
              {stats.studentsList.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-sm text-gray-400">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PhysicalAnalysis;

